import { create } from "zustand";
import {
  GAME,
  IAP,
  LEGACY_SKIN_PRICES,
  STARTER_PACK_COINS,
  STORAGE_KEY,
  WELCOME_COINS,
  WELCOME_GIFT_KEY,
} from "./constants";
import {
  DIFFICULTIES,
  isDifficultyUnlocked,
  runSpeed,
  type DifficultyId,
} from "./difficulty";
import {
  DEFAULT_VEHICLE_ID,
  STARTER_PACK_VEHICLE,
  VEHICLE_BY_ID,
  VEHICLES,
  isVehicleId,
  type VehicleDef,
  type VehicleId,
} from "./vehicles";
import {
  DEFAULT_STAGE_ID,
  STAGE_BY_ID,
  isStageId,
  type StageDef,
  type StageId,
} from "./stages";
import { newRunSeed } from "./rng";
import {
  GHOST_MAX_SAMPLES,
  MISSION_REWARD,
  type GhostSample,
  type MissionId,
  localDayKey,
  missionsForDay,
  prevDayKey,
  streakReward,
} from "./dailies";
import {
  isCloudEmpty,
  mergeCloud,
  type CloudSave,
} from "./cloudSave";
import { pullSave, schedulePush } from "../lib/cloud";
import { isShotMode } from "../lib/shotMode";

export type Phase = "menu" | "playing" | "dead" | "result";

export type DuelState = {
  rivalName: string;
  rivalDistance: number;
  seed: string;
  difficulty: DifficultyId;
  stage: StageId;
  mode: "ranks" | "hotseat";
};

export type DuelOutcome = "win" | "lose" | "draw" | null;

type Persist = {
  coins: number;
  vehiclesOwned: VehicleId[];
  equippedVehicle: VehicleId;
  stagesOwned: StageId[];
  equippedStage: StageId;
  removeAds: boolean;
  starterOwned: boolean;
  /** Personal bests per difficulty */
  bestByDifficulty: Record<DifficultyId, number>;
  gamesPlayed: number;
  /** Completed runs since last interstitial (0..INTERSTITIAL_EVERY-1) */
  runsSinceAd: number;
  difficulty: DifficultyId;
  playerId: string;
  playerName: string;
  /** Apple Sign In subject — when set, leaderboard uses this id. */
  appleUserId: string | null;
  /** First-run ✦100 claimed. */
  welcomeGiftClaimed: boolean;
  /** Never ask for a pseudo again (saved or Apple-linked). */
  nameChosen: boolean;
  /** When true, active vehicle powers fire automatically when ready. */
  powerAuto: boolean;
  /** Show personal-best ghost during a run. */
  ghostOn: boolean;
  streakCount: number;
  streakLastDay: string;
  streakClaimedDay: string;
  dailyDay: string;
  dailyProgress: Partial<Record<MissionId, number>>;
  dailyClaimed: MissionId[];
  ghostByDifficulty: Partial<Record<DifficultyId, GhostSample[]>>;
};

type RunState = {
  phase: Phase;
  distance: number;
  coinsRun: number;
  speed: number;
  runElapsed: number;
  lane: number;
  continuesUsed: number;
  showContinue: boolean;
  lastCrashAt: number;
  invulnerableUntil: number;
  clearToken: number;
  shake: number;
  /** True when the run that just ended set a new personal best. */
  lastRunWasBest: boolean;
  /** Jump tokens held (0..maxJumpsHeld). */
  jumps: number;
  /** Seconds left airborne; >0 = jumping. */
  jumpTimer: number;
  /** Duration of the current jump (for arc math). */
  jumpDuration: number;
  /** performance.now() until which active power is on. */
  powerActiveUntil: number;
  /** performance.now() when active power is ready again. */
  powerReadyAt: number;
  /** Samples recorded this run for a potential PB ghost. */
  ghostDraft: GhostSample[];
  /** Menu garage open — unmount the track Canvas so iOS has one WebGL context. */
  garageOpen: boolean;
  /** False until persist is read — don't flash the welcome gift on every boot. */
  hydrated: boolean;
  paused: boolean;
  /** performance.now() when pause started — freeze power / i-frames. */
  pauseHeldAt: number;
  /** Seeded road for this run (replay / duel). */
  runSeed: string;
  /** Active duel target; null = casual run. */
  duel: DuelState | null;
  duelOutcome: DuelOutcome;
  /** Menu: waiting for player 2 on the same seed. */
  pendingDuel: DuelState | null;
  /** Garage test drive — overrides equipped vehicle, no rewards. */
  practiceVehicle: VehicleId | null;
  /** Re-open garage on this ride after a test drive. */
  resumeGarageVehicle: VehicleId | null;
};

type Actions = {
  hydrate: () => void;
  persist: () => void;
  applyCloudSave: (remote: CloudSave) => void;
  setDifficulty: (d: DifficultyId) => void;
  setPlayerName: (name: string) => void;
  /** Bind Apple Sign In — replaces leaderboard identity. */
  linkAppleAccount: (appleUserId: string, suggestedName?: string) => void;
  unlinkAppleAccount: () => void;
  claimWelcomeGift: () => boolean;
  setGarageOpen: (open: boolean) => void;
  pause: () => void;
  resume: () => void;
  startRun: (opts?: {
    seed?: string;
    duel?: DuelState | null;
    practiceVehicle?: VehicleId;
  }) => void;
  queueHotseat: () => void;
  clearPendingDuel: () => void;
  setPendingDuel: (d: DuelState | null) => void;
  setLane: (lane: number) => void;
  nudgeLane: (dir: -1 | 1) => void;
  tick: (dt: number) => void;
  addRunCoin: () => void;
  addJumpToken: () => void;
  /** Consume one jump token. Returns false if none / already airborne. */
  useJump: () => boolean;
  /** Activate equipped vehicle active power. */
  usePower: () => boolean;
  isPowerActive: () => boolean;
  setPowerAuto: (on: boolean) => void;
  setGhostOn: (on: boolean) => void;
  crash: () => void;
  useContinue: () => void;
  skipContinue: () => void;
  finishToResult: () => void;
  backToMenu: () => void;
  clearResumeGarage: () => void;
  unlockVehicle: (id: VehicleId) => boolean;
  equipVehicle: (id: VehicleId) => void;
  unlockStage: (id: StageId) => boolean;
  equipStage: (id: StageId) => void;
  applyRemoveAds: () => void;
  applyStarterPack: () => void;
  grantRewardedCoins: (n: number) => void;
  setShake: (n: number) => void;
  setRunsSinceAd: (n: number) => void;
  personalBest: () => number;
  /** Leaderboard id — Apple subject when linked, else local playerId. */
  leaderboardId: () => string;
  /** Roll daily window if the calendar day changed. */
  ensureDaily: () => void;
  claimStreak: () => number;
  claimMission: (id: MissionId) => number;
  recordGhostSample: (sample: GhostSample) => void;
};

function newPlayerId() {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `p_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
}

const defaultPersist = (): Persist => ({
  coins: 40,
  vehiclesOwned: [DEFAULT_VEHICLE_ID],
  equippedVehicle: DEFAULT_VEHICLE_ID,
  stagesOwned: [DEFAULT_STAGE_ID],
  equippedStage: DEFAULT_STAGE_ID,
  removeAds: false,
  starterOwned: false,
  bestByDifficulty: { easy: 0, medium: 0, hard: 0 },
  gamesPlayed: 0,
  runsSinceAd: 0,
  difficulty: "easy",
  playerId: newPlayerId(),
  playerName: "",
  appleUserId: null,
  welcomeGiftClaimed: false,
  nameChosen: false,
  powerAuto: false,
  ghostOn: true,
  streakCount: 0,
  streakLastDay: "",
  streakClaimedDay: "",
  dailyDay: "",
  dailyProgress: {},
  dailyClaimed: [],
  ghostByDifficulty: {},
});

function readWelcomeGiftFlag(): boolean {
  try {
    return localStorage.getItem(WELCOME_GIFT_KEY) === "1";
  } catch {
    return false;
  }
}

function writeWelcomeGiftFlag() {
  try {
    localStorage.setItem(WELCOME_GIFT_KEY, "1");
  } catch {
    /* ignore */
  }
}

/** True if this save already had (or should skip) the one-shot launch bonus. */
export function inferWelcomeGiftClaimed(opts: {
  explicit?: boolean;
  standaloneFlag?: boolean;
  gamesPlayed: number;
  coins: number;
  startingCoins?: number;
  extraVehicles?: boolean;
}): boolean {
  if (opts.explicit) return true;
  if (opts.standaloneFlag) return true;
  if (opts.gamesPlayed > 0) return true;
  if (opts.coins > (opts.startingCoins ?? 40)) return true;
  if (opts.extraVehicles) return true;
  return false;
}

function migrate(raw: Record<string, unknown>): Persist {
  const base = defaultPersist();
  const incoming = {
    ...((raw.bestByDifficulty as Record<string, number>) || {}),
  };
  // old key "normal" → medium
  if (typeof incoming.normal === "number") {
    incoming.medium = Math.max(incoming.medium ?? 0, incoming.normal);
    delete incoming.normal;
  }
  const bestByDifficulty = {
    ...base.bestByDifficulty,
    ...incoming,
  } as Persist["bestByDifficulty"];
  if (typeof raw.bestDistance === "number" && !raw.bestByDifficulty) {
    bestByDifficulty.medium = Math.max(
      bestByDifficulty.medium,
      Math.floor(raw.bestDistance as number),
    );
  }
  let difficulty: DifficultyId = "easy";
  if (
    raw.difficulty === "easy" ||
    raw.difficulty === "hard" ||
    raw.difficulty === "medium"
  ) {
    difficulty = raw.difficulty;
  } else if (raw.difficulty === "normal") {
    difficulty = "medium";
  }

  const gamesPlayed = Math.max(0, Math.floor(Number(raw.gamesPlayed) || 0));
  if (!isDifficultyUnlocked(difficulty, gamesPlayed)) {
    difficulty = "easy";
  }

  const starterOwned = Boolean(raw.starterOwned);
  let coins = typeof raw.coins === "number" ? raw.coins : base.coins;

  // Migrate from skin system → refund spent coins, unlock vehicles.
  let vehiclesOwned: VehicleId[] = [DEFAULT_VEHICLE_ID];
  if (Array.isArray(raw.vehiclesOwned)) {
    vehiclesOwned = [
      DEFAULT_VEHICLE_ID,
      ...raw.vehiclesOwned.filter(isVehicleId),
    ];
  } else if (Array.isArray(raw.unlocked)) {
    // Legacy skins: refund purchase prices so players aren't robbed.
    for (const id of raw.unlocked as string[]) {
      const refund = LEGACY_SKIN_PRICES[id] ?? 0;
      coins += refund;
    }
    if (starterOwned) vehiclesOwned.push(STARTER_PACK_VEHICLE);
  }

  if (starterOwned && !vehiclesOwned.includes(STARTER_PACK_VEHICLE)) {
    vehiclesOwned.push(STARTER_PACK_VEHICLE);
  }
  vehiclesOwned = [...new Set(vehiclesOwned)];

  let equippedVehicle: VehicleId = DEFAULT_VEHICLE_ID;
  if (isVehicleId(raw.equippedVehicle) && vehiclesOwned.includes(raw.equippedVehicle)) {
    equippedVehicle = raw.equippedVehicle;
  } else if (starterOwned && vehiclesOwned.includes(STARTER_PACK_VEHICLE)) {
    equippedVehicle = STARTER_PACK_VEHICLE;
  }

  let stagesOwned: StageId[] = [DEFAULT_STAGE_ID];
  if (Array.isArray(raw.stagesOwned)) {
    stagesOwned = [
      DEFAULT_STAGE_ID,
      ...raw.stagesOwned.filter(isStageId),
    ];
  }
  stagesOwned = [...new Set(stagesOwned)];
  let equippedStage: StageId = DEFAULT_STAGE_ID;
  if (isStageId(raw.equippedStage) && stagesOwned.includes(raw.equippedStage)) {
    equippedStage = raw.equippedStage;
  }

  const appleUserId =
    typeof raw.appleUserId === "string" && raw.appleUserId.length >= 8
      ? raw.appleUserId
      : (() => {
          try {
            const backup = localStorage.getItem("lanedash.appleUserId");
            return backup && backup.length >= 8 ? backup : null;
          } catch {
            return null;
          }
        })();

  let playerName = typeof raw.playerName === "string" ? raw.playerName : "";
  if (!playerName.trim()) {
    try {
      playerName = localStorage.getItem("lanedash.playerName") || "";
    } catch {
      /* ignore */
    }
  }
  if (appleUserId && !playerName.trim()) playerName = "Courier";

  const streakCount = Math.max(0, Math.floor(Number(raw.streakCount) || 0));
  const streakLastDay =
    typeof raw.streakLastDay === "string" ? raw.streakLastDay : "";
  const streakClaimedDay =
    typeof raw.streakClaimedDay === "string" ? raw.streakClaimedDay : "";
  const dailyDay = typeof raw.dailyDay === "string" ? raw.dailyDay : "";
  const dailyProgress =
    raw.dailyProgress && typeof raw.dailyProgress === "object"
      ? (raw.dailyProgress as Persist["dailyProgress"])
      : {};
  const dailyClaimed = Array.isArray(raw.dailyClaimed)
    ? (raw.dailyClaimed as MissionId[])
    : [];
  const ghostByDifficulty =
    raw.ghostByDifficulty && typeof raw.ghostByDifficulty === "object"
      ? (raw.ghostByDifficulty as Persist["ghostByDifficulty"])
      : {};

  return {
    ...base,
    coins,
    vehiclesOwned,
    equippedVehicle,
    stagesOwned,
    equippedStage,
    removeAds: Boolean(raw.removeAds) || starterOwned,
    starterOwned,
    bestByDifficulty,
    gamesPlayed,
    runsSinceAd: Math.max(0, Math.floor(Number(raw.runsSinceAd) || 0)),
    difficulty,
    playerId:
      appleUserId ||
      (typeof raw.playerId === "string" && raw.playerId.length >= 8
        ? raw.playerId
        : base.playerId),
    playerName: playerName.trim(),
    appleUserId,
    welcomeGiftClaimed: inferWelcomeGiftClaimed({
      explicit: Boolean(raw.welcomeGiftClaimed),
      standaloneFlag: readWelcomeGiftFlag(),
      gamesPlayed,
      coins,
      startingCoins: base.coins,
      extraVehicles: vehiclesOwned.length > 1,
    }),
    nameChosen:
      Boolean(raw.nameChosen) ||
      Boolean(playerName.trim()) ||
      Boolean(appleUserId),
    powerAuto: Boolean(raw.powerAuto),
    ghostOn: raw.ghostOn !== false,
    streakCount,
    streakLastDay,
    streakClaimedDay,
    dailyDay,
    dailyProgress,
    dailyClaimed,
    ghostByDifficulty,
  };
}

function load(): Persist {
  try {
    const raw =
      localStorage.getItem(STORAGE_KEY) ||
      localStorage.getItem("lanedash.v4") ||
      localStorage.getItem("lanedash.v3") ||
      localStorage.getItem("lanedash.v2") ||
      localStorage.getItem("lanedash.v1");
    if (!raw) return defaultPersist();
    return migrate(JSON.parse(raw) as Record<string, unknown>);
  } catch {
    return defaultPersist();
  }
}

function shiftDeadline(until: number, pauseAt: number, now: number) {
  if (until <= pauseAt) return until;
  return now + (until - pauseAt);
}

function snapshot(s: Persist): CloudSave {
  return {
    playerId: s.playerId,
    appleUserId: s.appleUserId,
    playerName: s.playerName,
    coins: s.coins,
    vehiclesOwned: s.vehiclesOwned,
    equippedVehicle: s.equippedVehicle,
    stagesOwned: s.stagesOwned,
    equippedStage: s.equippedStage,
    removeAds: s.removeAds,
    starterOwned: s.starterOwned,
    bestByDifficulty: s.bestByDifficulty,
    gamesPlayed: s.gamesPlayed,
    runsSinceAd: s.runsSinceAd,
    difficulty: s.difficulty,
    welcomeGiftClaimed: s.welcomeGiftClaimed,
    nameChosen: s.nameChosen,
    powerAuto: s.powerAuto,
    ghostOn: s.ghostOn,
    streakCount: s.streakCount,
    streakLastDay: s.streakLastDay,
    streakClaimedDay: s.streakClaimedDay,
    dailyDay: s.dailyDay,
    dailyProgress: s.dailyProgress as Record<string, number>,
    dailyClaimed: s.dailyClaimed,
    ghostByDifficulty: s.ghostByDifficulty as Record<string, unknown>,
  };
}

let cloudReady = false;
let cloudBusy = false;

async function bootstrapCloud(
  get: () => Persist & RunState & Actions,
  set: (partial: Partial<Persist>) => void,
) {
  if (isShotMode()) return;
  if (cloudReady || cloudBusy) return;
  cloudBusy = true;
  try {
    const local = snapshot(get());
    const remote = await pullSave(local.playerId, local.appleUserId);
    if (remote === undefined) return;
    if (remote && !isCloudEmpty(remote)) {
      const merged = mergeCloud(snapshot(get()), remote);
      set(merged as Partial<Persist>);
    }
    cloudReady = true;
    get().persist();
  } finally {
    cloudBusy = false;
  }
}

export const useGame = create<Persist & RunState & Actions>((set, get) => ({
  ...defaultPersist(),
  phase: "menu",
  distance: 0,
  coinsRun: 0,
  speed: DIFFICULTIES.medium.baseSpeed,
  runElapsed: 0,
  lane: 1,
  continuesUsed: 0,
  showContinue: false,
  lastCrashAt: 0,
  invulnerableUntil: 0,
  clearToken: 0,
  shake: 0,
  lastRunWasBest: false,
  jumps: GAME.startJumps,
  jumpTimer: 0,
  jumpDuration: GAME.jumpDuration,
  powerActiveUntil: 0,
  powerReadyAt: 0,
  ghostDraft: [],
  garageOpen: false,
  hydrated: false,
  paused: false,
  pauseHeldAt: 0,
  runSeed: "",
  duel: null,
  duelOutcome: null,
  pendingDuel: null,
  practiceVehicle: null,
  resumeGarageVehicle: null,
  setGarageOpen: (open) => set({ garageOpen: open }),
  hydrate: () => {
    const loaded = load();
    if (loaded.welcomeGiftClaimed) writeWelcomeGiftFlag();
    set({ ...loaded, hydrated: true });
    get().ensureDaily();
    get().persist();
  },
  persist: () => {
    if (!get().hydrated) return;
    const s = get();
    const data: Persist = {
      coins: s.coins,
      vehiclesOwned: s.vehiclesOwned,
      equippedVehicle: s.equippedVehicle,
      stagesOwned: s.stagesOwned,
      equippedStage: s.equippedStage,
      removeAds: s.removeAds,
      starterOwned: s.starterOwned,
      bestByDifficulty: s.bestByDifficulty,
      gamesPlayed: s.gamesPlayed,
      runsSinceAd: s.runsSinceAd,
      difficulty: s.difficulty,
      playerId: s.playerId,
      playerName: s.playerName,
      appleUserId: s.appleUserId,
      welcomeGiftClaimed: s.welcomeGiftClaimed,
      nameChosen: s.nameChosen,
      powerAuto: s.powerAuto,
      ghostOn: s.ghostOn,
      streakCount: s.streakCount,
      streakLastDay: s.streakLastDay,
      streakClaimedDay: s.streakClaimedDay,
      dailyDay: s.dailyDay,
      dailyProgress: s.dailyProgress,
      dailyClaimed: s.dailyClaimed,
      ghostByDifficulty: s.ghostByDifficulty,
    };
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      if (s.welcomeGiftClaimed) writeWelcomeGiftFlag();
      if (s.appleUserId) {
        localStorage.setItem("lanedash.appleUserId", s.appleUserId);
      } else {
        localStorage.removeItem("lanedash.appleUserId");
      }
      if (s.playerName.trim()) {
        localStorage.setItem("lanedash.playerName", s.playerName.trim());
      }
    } catch {
      /* quota / private mode — keep in-memory state */
    }
    if (!isShotMode()) {
      if (cloudReady) schedulePush(() => snapshot(get()));
      else void bootstrapCloud(get, set);
    }
  },
  applyCloudSave: (remote) => {
    if (!get().hydrated) return;
    set({
      playerId: remote.playerId || get().playerId,
      appleUserId: remote.appleUserId ?? get().appleUserId,
      playerName: remote.playerName || get().playerName,
      coins: remote.coins,
      vehiclesOwned: remote.vehiclesOwned,
      equippedVehicle: remote.equippedVehicle,
      stagesOwned: remote.stagesOwned,
      equippedStage: remote.equippedStage,
      removeAds: remote.removeAds,
      starterOwned: remote.starterOwned,
      bestByDifficulty: remote.bestByDifficulty,
      gamesPlayed: remote.gamesPlayed,
      runsSinceAd: remote.runsSinceAd,
      difficulty: remote.difficulty,
      welcomeGiftClaimed: remote.welcomeGiftClaimed,
      nameChosen: remote.nameChosen,
      powerAuto: remote.powerAuto,
      ghostOn: remote.ghostOn,
      streakCount: remote.streakCount,
      streakLastDay: remote.streakLastDay,
      streakClaimedDay: remote.streakClaimedDay,
      dailyDay: remote.dailyDay,
      dailyProgress: remote.dailyProgress as Persist["dailyProgress"],
      dailyClaimed: remote.dailyClaimed as Persist["dailyClaimed"],
      ghostByDifficulty: remote.ghostByDifficulty as Persist["ghostByDifficulty"],
    });
    cloudReady = true;
    get().persist();
  },

  ensureDaily: () => {
    if (!get().hydrated) return;
    const today = localDayKey();
    const s = get();
    if (s.dailyDay === today) return;
    set({
      dailyDay: today,
      dailyProgress: {},
      dailyClaimed: [],
    });
    get().persist();
  },

  claimStreak: () => {
    get().ensureDaily();
    const s = get();
    const today = localDayKey();
    if (s.streakCount <= 0) return 0;
    if (s.streakLastDay !== today) return 0;
    if (s.streakClaimedDay === today) return 0;
    const reward = streakReward(s.streakCount);
    set({
      coins: s.coins + reward,
      streakClaimedDay: today,
    });
    get().persist();
    return reward;
  },

  claimMission: (id) => {
    get().ensureDaily();
    const s = get();
    const today = localDayKey();
    const defs = missionsForDay(today);
    const def = defs.find((m) => m.id === id);
    if (!def) return 0;
    if (s.dailyClaimed.includes(id)) return 0;
    const progress = s.dailyProgress[id] ?? 0;
    if (progress < def.target) return 0;
    set({
      coins: s.coins + MISSION_REWARD,
      dailyClaimed: [...s.dailyClaimed, id],
    });
    get().persist();
    return MISSION_REWARD;
  },

  recordGhostSample: (sample) => {
    if (get().practiceVehicle) return;
    const draft = get().ghostDraft;
    if (draft.length >= GHOST_MAX_SAMPLES) return;
    // Keep roughly GHOST_SAMPLE_HZ by dropping if last sample is too close.
    const last = draft[draft.length - 1];
    if (last && sample.d - last.d < 0.6) return;
    set({ ghostDraft: [...draft, sample] });
  },

  setDifficulty: (d) => {
    if (get().pendingDuel) return;
    if (!isDifficultyUnlocked(d, get().gamesPlayed)) return;
    set({ difficulty: d });
    get().persist();
  },

  setPlayerName: (name) => {
    const clean = name.replace(/[^\p{L}\p{N} _.-]/gu, "").trim().slice(0, 16);
    if (!clean) return;
    set({ playerName: clean, nameChosen: true });
    get().persist();
  },

  linkAppleAccount: (appleUserId, suggestedName) => {
    const id = String(appleUserId || "").trim().slice(0, 128);
    if (id.length < 8) return;
    const patch: Partial<Persist> = {
      appleUserId: id,
      playerId: id,
      nameChosen: true,
    };
    const existing = get().playerName.trim();
    if (!existing) {
      const fromApple = suggestedName
        ?.replace(/[^\p{L}\p{N} _.-]/gu, "")
        .trim()
        .slice(0, 16);
      patch.playerName = fromApple || "Courier";
    }
    set(patch);
    get().persist();
  },

  unlinkAppleAccount: () => {
    set({ appleUserId: null });
    get().persist();
  },

  claimWelcomeGift: () => {
    const s = get();
    if (s.welcomeGiftClaimed) return false;
    writeWelcomeGiftFlag();
    set({
      coins: s.coins + WELCOME_COINS,
      welcomeGiftClaimed: true,
    });
    get().persist();
    return true;
  },

  leaderboardId: () => get().appleUserId || get().playerId,

  personalBest: () => get().bestByDifficulty[get().difficulty] ?? 0,

  pause: () => {
    const s = get();
    if (s.phase !== "playing" || s.paused) return;
    set({ paused: true, pauseHeldAt: performance.now() });
  },

  resume: () => {
    const s = get();
    if (!s.paused) return;
    const now = performance.now();
    const at = s.pauseHeldAt || now;
    set({
      paused: false,
      pauseHeldAt: 0,
      powerActiveUntil: shiftDeadline(s.powerActiveUntil, at, now),
      powerReadyAt: shiftDeadline(s.powerReadyAt, at, now),
      invulnerableUntil: shiftDeadline(s.invulnerableUntil, at, now),
    });
  },

  startRun: (opts) => {
    get().ensureDaily();
    const practiceVehicle =
      opts?.practiceVehicle && isVehicleId(opts.practiceVehicle)
        ? opts.practiceVehicle
        : null;
    const pending = practiceVehicle
      ? null
      : opts?.duel !== undefined
        ? opts.duel
        : get().pendingDuel;
    const duel = pending ?? null;
    if (duel) {
      set({ difficulty: duel.difficulty, pendingDuel: null });
    }
    const diff = DIFFICULTIES[get().difficulty];
    const v = practiceVehicle
      ? (VEHICLE_BY_ID[practiceVehicle] ?? equippedVehicle())
      : equippedVehicle();
    const runSeed = opts?.seed || duel?.seed || newRunSeed();
    set({
      phase: "playing",
      distance: 0,
      coinsRun: 0,
      speed: diff.baseSpeed * v.speedMult,
      runElapsed: 0,
      lane: 1,
      continuesUsed: 0,
      showContinue: false,
      lastCrashAt: 0,
      invulnerableUntil: performance.now() + 900,
      clearToken: get().clearToken + 1,
      shake: 0,
      jumps: GAME.startJumps,
      jumpTimer: 0,
      jumpDuration:
        GAME.jumpDuration *
        v.jumpMult *
        (duel ? (STAGE_BY_ID[duel.stage] ?? equippedStage()) : equippedStage())
          .jumpMult,
      powerActiveUntil: 0,
      powerReadyAt: 0,
      gamesPlayed: practiceVehicle ? get().gamesPlayed : get().gamesPlayed + 1,
      ghostDraft: [],
      paused: false,
      pauseHeldAt: 0,
      runSeed,
      duel: practiceVehicle ? null : duel,
      duelOutcome: null,
      practiceVehicle,
      garageOpen: false,
      resumeGarageVehicle: null,
    });
    if (!practiceVehicle) get().persist();
  },

  setLane: (lane) => {
    if (get().phase !== "playing" || get().paused) return;
    set({ lane: Math.max(0, Math.min(2, lane)) });
  },

  nudgeLane: (dir) => {
    if (get().phase !== "playing" || get().paused) return;
    const next = Math.max(0, Math.min(2, get().lane + dir));
    if (next !== get().lane) set({ lane: next });
  },

  tick: (dt) => {
    if (get().phase !== "playing" || get().paused) return;
    const diff = DIFFICULTIES[get().difficulty];
    const v = equippedVehicle();
    const runElapsed = get().runElapsed + dt;
    const distance = get().distance + get().speed * dt;
    const base = runSpeed(diff, runElapsed, distance);
    const speed = base * v.speedMult;
    set({
      runElapsed,
      speed,
      distance,
      jumpTimer: Math.max(0, get().jumpTimer - dt),
      shake: Math.max(0, get().shake - dt * 4),
    });
  },

  addRunCoin: () => {
    const diffMult = DIFFICULTIES[get().difficulty].coinRewardMult;
    const vehicleMult = equippedVehicle().coinMult;
    const stageMult = activeStage().coinMult;
    const add = Math.max(1, Math.round(diffMult * vehicleMult * stageMult));
    if (get().practiceVehicle) {
      set({ coinsRun: get().coinsRun + add });
      return;
    }
    get().ensureDaily();
    const progress = { ...get().dailyProgress };
    progress.coins20 = (progress.coins20 ?? 0) + add;
    set({ coinsRun: get().coinsRun + add, dailyProgress: progress });
  },

  addJumpToken: () => {
    set({ jumps: Math.min(GAME.maxJumpsHeld, get().jumps + 1) });
  },

  useJump: () => {
    const s = get();
    if (s.phase !== "playing" || s.paused) return false;
    if (s.jumps <= 0 || s.jumpTimer > 0) return false;
    const duration =
      GAME.jumpDuration * equippedVehicle().jumpMult * activeStage().jumpMult;
    if (get().practiceVehicle) {
      set({
        jumps: s.jumps - 1,
        jumpTimer: duration,
        jumpDuration: duration,
      });
      return true;
    }
    get().ensureDaily();
    const progress = { ...get().dailyProgress };
    progress.jumps8 = (progress.jumps8 ?? 0) + 1;
    set({
      jumps: s.jumps - 1,
      jumpTimer: duration,
      jumpDuration: duration,
      dailyProgress: progress,
    });
    return true;
  },

  usePower: () => {
    const s = get();
    if (s.phase !== "playing" || s.paused) return false;
    const v = equippedVehicle();
    if (v.power.type !== "active") return false;
    const now = performance.now();
    if (now < s.powerReadyAt) return false;
    if (now < s.powerActiveUntil) return false;
    const durationMs = (v.power.duration ?? 3) * 1000;
    const cooldownMs = (v.power.cooldown ?? 20) * 1000;
    set({
      powerActiveUntil: now + durationMs,
      powerReadyAt: now + cooldownMs,
    });
    return true;
  },

  isPowerActive: () => {
    const s = get();
    const now = s.paused ? s.pauseHeldAt : performance.now();
    return now < s.powerActiveUntil;
  },

  setPowerAuto: (on) => {
    set({ powerAuto: on });
    get().persist();
  },

  setGhostOn: (on) => {
    set({ ghostOn: on });
    get().persist();
  },

  crash: () => {
    const s = get();
    if (s.phase !== "playing" || s.paused) return;
    if (performance.now() < s.invulnerableUntil) return;
    const canContinue =
      !s.duel && !s.practiceVehicle && s.continuesUsed < GAME.maxContinuesPerRun;
    set({
      phase: "dead",
      showContinue: canContinue,
      lastCrashAt: performance.now(),
      shake: 1,
      powerActiveUntil: 0,
      paused: false,
      pauseHeldAt: 0,
    });
  },

  useContinue: () => {
    set({
      phase: "playing",
      continuesUsed: get().continuesUsed + 1,
      showContinue: false,
      invulnerableUntil: performance.now() + GAME.invulnSeconds * 1000,
      clearToken: get().clearToken + 1,
      shake: 0,
      jumpTimer: 0,
      powerActiveUntil: 0,
      paused: false,
      pauseHeldAt: 0,
    });
  },

  skipContinue: () => get().finishToResult(),

  finishToResult: () => {
    get().ensureDaily();
    const s = get();
    if (s.practiceVehicle) {
      set({
        phase: "result",
        showContinue: false,
        powerActiveUntil: 0,
        lastRunWasBest: false,
        ghostDraft: [],
        paused: false,
        pauseHeldAt: 0,
        duelOutcome: null,
      });
      return;
    }
    const score = Math.floor(s.distance);
    const prev = s.bestByDifficulty[s.difficulty] ?? 0;
    const lastRunWasBest = score > prev;
    const bestByDifficulty = {
      ...s.bestByDifficulty,
      [s.difficulty]: Math.max(prev, score),
    };

    const today = localDayKey();
    let streakCount = s.streakCount;
    let streakLastDay = s.streakLastDay;
    if (streakLastDay !== today) {
      if (streakLastDay === prevDayKey(today)) {
        streakCount = Math.max(1, streakCount) + 1;
      } else {
        streakCount = 1;
      }
      streakLastDay = today;
    }

    const progress = { ...s.dailyProgress };
    progress.play3 = (progress.play3 ?? 0) + 1;
    if (score >= 400) {
      progress.reach400 = Math.max(progress.reach400 ?? 0, score);
    }
    if (lastRunWasBest) {
      progress.beatBest = 1;
    }

    const ghostByDifficulty = { ...s.ghostByDifficulty };
    if (lastRunWasBest && s.ghostDraft.length >= 8) {
      ghostByDifficulty[s.difficulty] = s.ghostDraft.slice(0, GHOST_MAX_SAMPLES);
    }

    let duelOutcome: DuelOutcome = null;
    if (s.duel) {
      if (score > s.duel.rivalDistance) duelOutcome = "win";
      else if (score < s.duel.rivalDistance) duelOutcome = "lose";
      else duelOutcome = "draw";
    }

    set({
      phase: "result",
      coins: s.coins + s.coinsRun,
      bestByDifficulty,
      showContinue: false,
      powerActiveUntil: 0,
      lastRunWasBest,
      streakCount,
      streakLastDay,
      dailyProgress: progress,
      ghostByDifficulty,
      ghostDraft: [],
      paused: false,
      pauseHeldAt: 0,
      duelOutcome,
    });
    get().persist();
  },

  backToMenu: () => {
    const practice = get().practiceVehicle;
    set({
      phase: "menu",
      showContinue: false,
      shake: 0,
      paused: false,
      pauseHeldAt: 0,
      duel: null,
      duelOutcome: null,
      practiceVehicle: null,
      resumeGarageVehicle: practice,
    });
  },
  clearResumeGarage: () => set({ resumeGarageVehicle: null }),

  queueHotseat: () => {
    const s = get();
    const seed = s.runSeed;
    if (!seed || Math.floor(s.distance) < 1) return;
    const name = s.playerName.trim() || "Courier";
    set({
      pendingDuel: {
        rivalName: name,
        rivalDistance: Math.floor(s.distance),
        seed,
        difficulty: s.difficulty,
        stage: s.duel?.stage ?? s.equippedStage,
        mode: "hotseat",
      },
    });
  },

  clearPendingDuel: () => set({ pendingDuel: null }),
  setPendingDuel: (d) =>
    set(d ? { pendingDuel: d, difficulty: d.difficulty } : { pendingDuel: null }),

  unlockVehicle: (id) => {
    const vehicle = VEHICLE_BY_ID[id];
    if (!vehicle) return false;
    const s = get();
    if (s.vehiclesOwned.includes(id)) return true;
    if (vehicle.iapExclusive && !s.starterOwned) return false;
    if (s.coins < vehicle.price) return false;
    set({
      coins: s.coins - vehicle.price,
      vehiclesOwned: [...s.vehiclesOwned, id],
      equippedVehicle: id,
    });
    get().persist();
    return true;
  },

  equipVehicle: (id) => {
    if (!get().vehiclesOwned.includes(id)) return;
    set({ equippedVehicle: id });
    get().persist();
  },

  unlockStage: (id) => {
    const stage = STAGE_BY_ID[id];
    if (!stage) return false;
    const s = get();
    if (s.stagesOwned.includes(id)) return true;
    if (s.coins < stage.price) return false;
    set({
      coins: s.coins - stage.price,
      stagesOwned: [...s.stagesOwned, id],
      equippedStage: id,
    });
    get().persist();
    return true;
  },

  equipStage: (id) => {
    if (!get().stagesOwned.includes(id)) return;
    set({ equippedStage: id });
    get().persist();
  },

  applyRemoveAds: () => {
    set({ removeAds: true });
    get().persist();
  },

  applyStarterPack: () => {
    const s = get();
    if (s.starterOwned) {
      set({ removeAds: true });
      get().persist();
      return;
    }
    const owned = new Set(s.vehiclesOwned);
    owned.add(STARTER_PACK_VEHICLE);
    set({
      starterOwned: true,
      coins: s.coins + STARTER_PACK_COINS,
      vehiclesOwned: [...owned],
      equippedVehicle: STARTER_PACK_VEHICLE,
      removeAds: true,
    });
    get().persist();
  },

  grantRewardedCoins: (n) => {
    set({ coins: get().coins + n });
    get().persist();
  },

  setShake: (n) => set({ shake: n }),

  setRunsSinceAd: (n) => {
    set({ runsSinceAd: n });
    get().persist();
  },
}));

export function equippedVehicle(): VehicleDef {
  const s = useGame.getState();
  const id = s.practiceVehicle ?? s.equippedVehicle;
  return VEHICLE_BY_ID[id] ?? VEHICLES[0];
}

export function equippedStage(): StageDef {
  const id = useGame.getState().equippedStage;
  return STAGE_BY_ID[id] ?? STAGE_BY_ID[DEFAULT_STAGE_ID];
}

export function activeStage(): StageDef {
  const s = useGame.getState();
  if (s.duel && STAGE_BY_ID[s.duel.stage]) return STAGE_BY_ID[s.duel.stage];
  return equippedStage();
}

export function activeDifficulty() {
  return DIFFICULTIES[useGame.getState().difficulty];
}

export { IAP };
